
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Modal,
    Image,
    Input,
    Row,
    Col,
    Pagination,
    message,
} from 'antd';
import 'bootstrap/dist/css/bootstrap.min.css';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import DOMPurify from 'dompurify';
import { allApiAction } from '../../Redux/common/action';
import { URL } from '../../Redux/common/url';
import { useParams } from 'react-router-dom';
import moment from 'moment';

// Convert HTML descriptions into clean plain text for cards.
const getPlainText = (html = '') => {
    if (!html) return '';

    const sanitizedHtml = DOMPurify.sanitize(String(html));
    const doc = new DOMParser().parseFromString(
        sanitizedHtml,
        'text/html'
    );

    return (doc.body.textContent || '')
        .replace(/\s+/g, ' ')
        .trim();
};

// Sanitize HTML before rendering formatted descriptions.
const getSafeHtml = (html = '') => ({
    __html: DOMPurify.sanitize(String(html)),
});

// Format amounts in Indian currency style.
const formatAmount = (amount) =>
    Number(amount || 0).toLocaleString('en-IN', {
        maximumFractionDigits: 2,
    });

const AddFunctionModal = ({ isShow, hideModal, orderDetails }) => {
    const { orderId } = useParams();
    const dispatch = useDispatch();

    const [selectedFunction, setSelectedFunction] = useState(null);
    const [selectedEssential, setSelectedEssential] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState({});
    const [detailsModalVisible, setDetailsModalVisible] = useState(false);
    const [selectedOptionForDetails, setSelectedOptionForDetails] =
        useState(null);
    const [guestNo, setGuestNo] = useState('');
    const [date, setDate] = useState(moment().format('YYYY-MM-DD'));

    const {
        functionList,
        currentPage,
        totalFunctions,
        essentialList,
        essentialTypesList,
    } = useSelector((state) => {
        const functionResponse =
            state?.allApi?.function_list || {};

        const functionPayload = functionResponse?.data;

        // Supports an array response and Laravel pagination.
        const paginationData = Array.isArray(functionPayload)
            ? functionResponse
            : functionPayload || {};

        return {
            functionList: Array.isArray(functionPayload)
                ? functionPayload
                : Array.isArray(paginationData?.data)
                  ? paginationData.data
                  : [],

            currentPage:
                Number(paginationData?.current_page) || 1,

            totalFunctions:
                Number(paginationData?.total) ||
                (Array.isArray(functionPayload)
                    ? functionPayload.length
                    : 0),

            essentialList:
                Array.isArray(state?.allApi?.essential_list?.data)
                    ? state.allApi.essential_list.data
                    : [],

            essentialTypesList:
                Array.isArray(
                    state?.allApi?.essential_type_list?.data
                )
                    ? state.allApi.essential_type_list.data
                    : [],
        };
    });

    // IDs of functions already included in the order.
    const existingFunctions = orderDetails?.functions || [];

    const existingFunctionIds = existingFunctions.map((item) =>
        Number(item.function_id ?? item.id)
    );

    const isAlreadyAdded = (func) =>
        existingFunctionIds.includes(Number(func?.id));

    // Fetch the first page when the modal opens.
    useEffect(() => {
        if (!isShow || !orderDetails?.occasion_id) return;

        setSelectedFunction(null);
        setSelectedEssential(null);
        setSelectedOptions({});
        setGuestNo('');
        setDate(moment().format('YYYY-MM-DD'));

        dispatch(
            allApiAction.getFunctions({
                page: 1,
                occasion_id: orderDetails.occasion_id,
            })
        );
    }, [isShow, orderDetails?.occasion_id, dispatch]);

    // Fetch a selected function page.
    const handleFunctionPageChange = (page) => {
        if (!orderDetails?.occasion_id) return;

        dispatch(
            allApiAction.getFunctions({
                page,
                occasion_id: orderDetails.occasion_id,
            })
        );
    };

    // Select any function, including an existing one.
    const handleFunctionSelect = (func) => {
        setSelectedFunction(func);
        setSelectedEssential(null);
        setSelectedOptions({});

        // Restore the existing values when editing a function.
        const existing = existingFunctions.find(
            (item) =>
                Number(item.function_id ?? item.id) ===
                Number(func.id)
        );

        if (existing) {
            setGuestNo(String(existing.guest_no ?? ''));
            setDate(
                existing.date
                    ? moment(existing.date).format('YYYY-MM-DD')
                    : moment().format('YYYY-MM-DD')
            );

            const restoredOptions = {};

            (existing.essentials || []).forEach((essential) => {
                restoredOptions[essential.essential_id] = (
                    essential.essential_types || []
                ).map((item) =>
                    Number(item.essential_type_id)
                );
            });

            setSelectedOptions(restoredOptions);
        } else {
            setGuestNo('');
            setDate(moment().format('YYYY-MM-DD'));
        }
    };

    // Fetch essentials after selecting a function.
    useEffect(() => {
        if (!selectedFunction || !orderDetails?.occasion_id) return;

        dispatch(
            allApiAction.getEssentials({
                page: 1,
                occasion_id: orderDetails.occasion_id,
                function_id: selectedFunction.id,
            })
        );
    }, [
        selectedFunction,
        dispatch,
        orderDetails?.occasion_id,
    ]);

    // Fetch options after selecting an essential.
    useEffect(() => {
        if (
            !selectedEssential ||
            !selectedFunction ||
            !orderDetails?.occasion_id
        ) {
            return;
        }

        dispatch(
            allApiAction.getEssentialTypes({
                page: 1,
                occasion_id: orderDetails.occasion_id,
                function_id: selectedFunction.id,
                essential_id: selectedEssential.id,
            })
        );
    }, [
        selectedEssential,
        dispatch,
        orderDetails?.occasion_id,
        selectedFunction?.id,
    ]);

    // Essential carousel settings.
    const essentialSliderSettings = {
        dots: true,
        infinite: essentialList.length > 2,
        speed: 500,
        slidesToShow: Math.min(3, essentialList.length || 1),
        slidesToScroll: 1,
        responsive: [
            { breakpoint: 768, settings: { slidesToShow: 2 } },
            { breakpoint: 576, settings: { slidesToShow: 1 } },
        ],
    };

    // Custom arrows for the details image carousel.
    const NextArrow = ({ onClick }) => (
        <div
            className="slick-arrow slick-next"
            style={{
                position: 'absolute',
                top: '50%',
                right: '10px',
                transform: 'translateY(-50%)',
                zIndex: 1,
                cursor: 'pointer',
                fontSize: '24px',
                color: '#000',
            }}
            onClick={onClick}
        />
    );

    const PrevArrow = ({ onClick }) => (
        <div
            className="slick-arrow slick-prev"
            style={{
                position: 'absolute',
                top: '50%',
                left: '10px',
                transform: 'translateY(-50%)',
                zIndex: 1,
                cursor: 'pointer',
                fontSize: '24px',
                color: '#000',
            }}
            onClick={onClick}
        />
    );

    const imageSliderSettings = {
        dots: false,
        infinite:
            (selectedOptionForDetails?.images?.length || 0) > 1,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
    };

    // Select or deselect an option.
    const handleSelectOption = (essentialId, essentialTypeId) => {
        setSelectedOptions((prev) => {
            const currentOptions = prev[essentialId] || [];

            if (currentOptions.includes(essentialTypeId)) {
                return {
                    ...prev,
                    [essentialId]: currentOptions.filter(
                        (id) => id !== essentialTypeId
                    ),
                };
            }

            return {
                ...prev,
                [essentialId]: [
                    ...currentOptions,
                    essentialTypeId,
                ],
            };
        });
    };

    const showDetailsModal = (option) => {
        setSelectedOptionForDetails(option);
        setDetailsModalVisible(true);
    };

    const hideDetailsModal = () => {
        setDetailsModalVisible(false);
        setSelectedOptionForDetails(null);
    };

    // Build the selected function's payload.
    const buildSelectedFunction = () => {
        if (!selectedFunction) return null;

        const totalFunctionAmount = Object.entries(
            selectedOptions
        ).reduce((total, [essentialId, typeIds]) => {
            const selectedTypes = (typeIds || []).map(Number);

            const matchingOptions = essentialTypesList.filter(
                (option) =>
                    selectedTypes.includes(Number(option.id)) &&
                    Number(selectedEssential?.id) ===
                        Number(essentialId)
            );

            return (
                total +
                matchingOptions.reduce(
                    (sum, option) =>
                        sum + (Number(option.amount) || 0),
                    0
                )
            );
        }, 0);

        return {
            function_id: selectedFunction.id,
            total_function_amount: totalFunctionAmount,
            guest_no: parseInt(guestNo, 10) || 0,
            date: date || '',
            essentials: Object.entries(selectedOptions)
                .filter(([, typeIds]) => typeIds?.length > 0)
                .map(([essentialId, typeIds]) => ({
                    essential_id: parseInt(essentialId, 10),
                    essential_types: typeIds.map((typeId) => ({
                        essential_type_id: typeId,
                    })),
                })),
        };
    };

    // Confirm and update the order without duplicating functions.
    const handleOk = () => {
        if (!selectedFunction) {
            message.warning('Please select a function first.');
            return;
        }

        const newFunction = buildSelectedFunction();

        if (!newFunction) return;

        const updatedFunctionId = Number(
            newFunction.function_id
        );

        const allFunctions = [];
        let replacedExisting = false;

        existingFunctions.forEach((func) => {
            const currentFunctionId = Number(
                func.function_id ?? func.id
            );

            const normalizedFunction = {
                function_id: func.function_id ?? func.id,
                total_function_amount:
                    Number(func.total_function_amount) || 0,
                guest_no: func.guest_no || 0,
                date: func.date || '',
                essentials: (func.essentials || []).map(
                    (essential) => ({
                        essential_id: essential.essential_id,
                        essential_types: (
                            essential.essential_types || []
                        ).map((type) => ({
                            essential_type_id:
                                type.essential_type_id,
                        })),
                    })
                ),
            };

            if (currentFunctionId === updatedFunctionId) {
                if (!replacedExisting) {
                    allFunctions.push(newFunction);
                    replacedExisting = true;
                }
                // Skip duplicates of this same function.
            } else {
                allFunctions.push(normalizedFunction);
            }
        });

        // Append only if this function was not already in the order.
        if (!replacedExisting) {
            allFunctions.push(newFunction);
        }

        const totalAmountFromFunctions = allFunctions.reduce(
            (sum, func) =>
                sum +
                (Number(func.total_function_amount) || 0),
            0
        );

        // Avoid adding the existing function total to the new
        // function total a second time.
        const otherOrderAmount =
            (Number(orderDetails?.total_amount) || 0) -
            existingFunctions.reduce(
                (sum, func) =>
                    sum +
                    (Number(func.total_function_amount) || 0),
                0
            );

        const formattedData = {
            order_id: orderId,
            occasion_id: orderDetails?.occasion_id,
            total_amount:
                totalAmountFromFunctions + otherOrderAmount,
            discount: orderDetails?.discount || 0,
            date: orderDetails?.date || '',
            remark: orderDetails?.remark || '',
            functions: allFunctions,
        };

        dispatch(allApiAction.updateOrder(formattedData));
        hideModal();
    };

    return (
        <div>
            <Modal
                key={isShow ? 'Modal Open' : 'Modal Close'}
                title="Add / Edit Function"
                open={isShow}
                onOk={handleOk}
                onCancel={hideModal}
                width={1000}
                okText="Confirm"
                cancelText="Cancel"
            >
                <div className="row">
                    {/* Functions list and pagination */}
                    <div className="col-md-3 border-end pe-3">
                        <h5 className="mb-3">Functions</h5>

                        {functionList.length > 0 ? (
                            functionList.map((func) => {
                                const active =
                                    Number(selectedFunction?.id) ===
                                    Number(func.id);

                                const alreadyAdded =
                                    isAlreadyAdded(func);

                                return (
                                    <div
                                        key={func.id}
                                        className="p-2 border mb-2 rounded"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() =>
                                            handleFunctionSelect(func)
                                        }
                                        onKeyDown={(event) => {
                                            if (
                                                event.key === 'Enter' ||
                                                event.key === ' '
                                            ) {
                                                event.preventDefault();
                                                handleFunctionSelect(func);
                                            }
                                        }}
                                        style={{
                                            cursor: 'pointer',
                                            backgroundColor: active
                                                ? '#e9ca77'
                                                : '#fff',
                                            borderColor: active
                                                ? '#b99a42'
                                                : undefined,
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-center gap-1">
                                            <span>{func.name}</span>

                                            {alreadyAdded && (
                                                <span
                                                    className="badge bg-success"
                                                    style={{
                                                        fontSize: '10px',
                                                    }}
                                                >
                                                    Added
                                                </span>
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            className={`btn btn-sm mt-2 ${
                                                active
                                                    ? 'btn-primary'
                                                    : 'btn-outline-primary'
                                            }`}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleFunctionSelect(func);
                                            }}
                                        >
                                            {active
                                                ? 'Selected'
                                                : alreadyAdded
                                                  ? 'Edit'
                                                  : 'Select'}
                                        </button>
                                    </div>
                                );
                            })
                        ) : (
                            <div>No Functions Found</div>
                        )}

                        {totalFunctions > 0 && (
                            <div className="d-flex justify-content-center mt-3 mb-3">
                                <Pagination
                                    current={currentPage}
                                    pageSize={10}
                                    total={totalFunctions}
                                    showSizeChanger={false}
                                    size="small"
                                    onChange={handleFunctionPageChange}
                                />
                            </div>
                        )}
                    </div>

                    {/* Essentials and options */}
                    <div className="col-md-9 ps-3">
                        {selectedFunction ? (
                            <>
                                <div
                                    className="p-3 mb-3 shadow-sm rounded"
                                    style={{
                                        backgroundColor: '#f8f9fa',
                                    }}
                                >
                                    <h5 className="mb-3">
                                        {isAlreadyAdded(selectedFunction)
                                            ? 'Edit Function: '
                                            : 'Essentials for: '}
                                        {selectedFunction.name}
                                    </h5>

                                    {isAlreadyAdded(selectedFunction) && (
                                        <p className="text-success small">
                                            This function is already on the
                                            order. Confirming will update it,
                                            not add another copy.
                                        </p>
                                    )}

                                    <Row
                                        gutter={[16, 8]}
                                        className="mb-3"
                                    >
                                        <Col span={12}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                }}
                                            >
                                                <span>Guests:</span>
                                                <Input
                                                    type="number"
                                                    value={guestNo}
                                                    onChange={(e) =>
                                                        setGuestNo(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Enter number of guests"
                                                    min={0}
                                                />
                                            </div>
                                        </Col>

                                        <Col span={12}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                }}
                                            >
                                                <span>Date:</span>
                                                <Input
                                                    type="date"
                                                    value={date}
                                                    onChange={(e) =>
                                                        setDate(
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                </div>

                                {essentialList.length > 0 ? (
                                    <>
                                        <Slider
                                            {...essentialSliderSettings}
                                        >
                                            {essentialList.map((ess) => (
                                                <div
                                                    key={ess.id}
                                                    className="px-2"
                                                >
                                                    <div
                                                        className={`card h-100 shadow-sm ${
                                                            Number(
                                                                selectedEssential?.id
                                                            ) === Number(ess.id)
                                                                ? 'border-primary'
                                                                : ''
                                                        }`}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() =>
                                                            setSelectedEssential(
                                                                ess
                                                            )
                                                        }
                                                        onKeyDown={(event) => {
                                                            if (
                                                                event.key ===
                                                                    'Enter' ||
                                                                event.key === ' '
                                                            ) {
                                                                event.preventDefault();
                                                                setSelectedEssential(
                                                                    ess
                                                                );
                                                            }
                                                        }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            borderWidth:
                                                                Number(
                                                                    selectedEssential?.id
                                                                ) ===
                                                                Number(ess.id)
                                                                    ? '2px'
                                                                    : '1px',
                                                            borderRadius:
                                                                '10px',
                                                        }}
                                                    >
                                                        <Image
                                                            src={
                                                                ess.image
                                                                    ? URL.API_BASE_URL +
                                                                      ess.image
                                                                    : '/placeholder.svg'
                                                            }
                                                            alt={ess.name}
                                                            height={150}
                                                            width="100%"
                                                            style={{
                                                                objectFit:
                                                                    'cover',
                                                                borderRadius:
                                                                    '10px 10px 0 0',
                                                            }}
                                                            preview={false}
                                                        />

                                                        <div className="card-body">
                                                            <h6 className="card-title">
                                                                {ess.name}
                                                            </h6>

                                                            <p className="card-text text-muted description-preview">
                                                                {getPlainText(
                                                                    ess.description
                                                                ) ||
                                                                    'No description available'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </Slider>

                                        {selectedEssential && (
                                            <div
                                                className="p-3 mt-4 shadow-sm rounded"
                                                style={{
                                                    backgroundColor: '#f8f9fa',
                                                }}
                                            >
                                                <h6 className="mb-3">
                                                    Options for:{' '}
                                                    {selectedEssential.name}
                                                </h6>

                                                {essentialTypesList.length > 0 ? (
                                                    <div className="row">
                                                        {essentialTypesList.map(
                                                            (option) => {
                                                                const isSelected = (
                                                                    selectedOptions[
                                                                        selectedEssential.id
                                                                    ] || []
                                                                ).includes(
                                                                    option.id
                                                                );

                                                                return (
                                                                    <div
                                                                        key={
                                                                            option.id
                                                                        }
                                                                        className="col-md-4 mb-3"
                                                                    >
                                                                        <div
                                                                            className="card h-100 shadow-sm"
                                                                            style={{
                                                                                borderRadius:
                                                                                    '10px',
                                                                                border: isSelected
                                                                                    ? '2px solid #198754'
                                                                                    : undefined,
                                                                            }}
                                                                        >
                                                                            <Image
                                                                                src={
                                                                                    option.feature_image
                                                                                        ? URL.API_BASE_URL +
                                                                                          option.feature_image
                                                                                        : '/placeholder.svg'
                                                                                }
                                                                                alt={
                                                                                    option.name
                                                                                }
                                                                                height={
                                                                                    100
                                                                                }
                                                                                width="100%"
                                                                                style={{
                                                                                    objectFit:
                                                                                        'cover',
                                                                                    borderRadius:
                                                                                        '10px 10px 0 0',
                                                                                }}
                                                                                preview={
                                                                                    false
                                                                                }
                                                                            />

                                                                            <div className="card-body">
                                                                                <h6 className="card-title">
                                                                                    {
                                                                                        option.name
                                                                                    }
                                                                                </h6>

                                                                                <p className="card-text text-muted description-preview">
                                                                                    {getPlainText(
                                                                                        option.description
                                                                                    ) ||
                                                                                        'No description'}
                                                                                </p>

                                                                                <div className="option-amount mb-3">
                                                                                    <span className="text-muted">
                                                                                        Amount:{' '}
                                                                                    </span>
                                                                                    <strong>
                                                                                        ₹
                                                                                        {formatAmount(
                                                                                            option.amount
                                                                                        )}
                                                                                    </strong>
                                                                                </div>

                                                                                <button
                                                                                    type="button"
                                                                                    className={`btn btn-sm ${
                                                                                        isSelected
                                                                                            ? 'btn-success'
                                                                                            : 'btn-outline-primary'
                                                                                    }`}
                                                                                    onClick={() =>
                                                                                        handleSelectOption(
                                                                                            selectedEssential.id,
                                                                                            option.id
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    {isSelected
                                                                                        ? 'Selected'
                                                                                        : 'Select'}
                                                                                </button>

                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-link btn-sm ms-2 p-0"
                                                                                    onClick={() =>
                                                                                        showDetailsModal(
                                                                                            option
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    View Details
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div>
                                                        No Options Available
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div>No Essentials Available</div>
                                )}
                            </>
                        ) : (
                            <div>
                                Select a function to view essentials and
                                options.
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Option Details Modal */}
            <Modal
                title={
                    selectedOptionForDetails?.name || 'Option Details'
                }
                open={detailsModalVisible}
                onOk={hideDetailsModal}
                onCancel={hideDetailsModal}
                footer={null}
                width={600}
            >
                {selectedOptionForDetails && (
                    <div>
                        {selectedOptionForDetails.images?.length > 0 && (
                            <Slider {...imageSliderSettings}>
                                {selectedOptionForDetails.images.map(
                                    (img, idx) => (
                                        <div
                                            key={img.id || idx}
                                            style={{
                                                position: 'relative',
                                            }}
                                        >
                                            <Image
                                                src={
                                                    img.image
                                                        ? URL.API_BASE_URL +
                                                          img.image
                                                        : '/placeholder.svg'
                                                }
                                                alt={
                                                    selectedOptionForDetails.name
                                                }
                                                width="100%"
                                                style={{
                                                    maxHeight: 300,
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        </div>
                                    )
                                )}
                            </Slider>
                        )}

                        <h5 className="mt-3">
                            {selectedOptionForDetails.name}
                        </h5>

                        <div
                            className="description-details"
                            dangerouslySetInnerHTML={getSafeHtml(
                                selectedOptionForDetails.description ||
                                    'No description available'
                            )}
                        />

                        <p className="mt-3">
                            <strong>Amount:</strong> ₹
                            {formatAmount(
                                selectedOptionForDetails.amount
                            )}
                        </p>
                    </div>
                )}
            </Modal>

            <style>
                {`
                    .description-preview {
                        display: -webkit-box;
                        -webkit-box-orient: vertical;
                        -webkit-line-clamp: 3;
                        line-clamp: 3;
                        overflow: hidden;
                        overflow-wrap: anywhere;
                        line-height: 1.5;
                        max-height: 4.5em;
                        margin-bottom: 12px;
                    }

                    .option-amount {
                        padding: 8px 10px;
                        background: #f8f9fa;
                        border-radius: 6px;
                        font-size: 16px;
                    }

                    .option-amount strong {
                        color: #198754;
                    }

                    .description-details {
                        overflow-wrap: anywhere;
                        line-height: 1.7;
                        max-height: 350px;
                        overflow-y: auto;
                    }

                    .description-details p {
                        margin-bottom: 12px;
                    }

                    .description-details ul,
                    .description-details ol {
                        padding-left: 24px;
                        margin-bottom: 12px;
                    }

                    .description-details img {
                        max-width: 100%;
                        height: auto;
                    }

                    .description-details table {
                        max-width: 100%;
                        border-collapse: collapse;
                    }
                `}
            </style>
        </div>
    );
};


export default AddFunctionModal;
